import React, { useState, useEffect } from 'react';
import axios from 'axios';
import RetentionCalculator from './RetentionCalculator';
import Footer from '../../layout/Footer';

// URLs Constants
const WEBHOOK_URL = 'https://services.leadconnectorhq.com/hooks/hz6swxxqV8ZMTuyTG0hP/webhook-trigger/3642bd2e-37ef-4eb7-9edd-4c634ae80008';
const VIDALYTICS_BASE_URL = 'https://fast.vidalytics.com/embeds/56hJ9E4N/XmGf65nZQpgCgcYW/';
const VIDALYTICS_EMBED_ID = 'vidalytics_embed_XmGf65nZQpgCgcYW';
const BOOKING_WIDGET_URL = 'https://api.leadconnectorhq.com/widget/booking/bM7RFpLdUbQLf8FzoA9m';
const FORM_EMBED_SCRIPT_URL = 'https://link.msgsndr.com/js/form_embed.js';

const PublicRetentionCalculator = () => {
    const [isUnlocked, setIsUnlocked] = useState(false);
    const [reportGenerated, setReportGenerated] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        title: ''
    });
    const [formErrors, setFormErrors] = useState({});

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
        // Clear error when user starts typing
        if (formErrors[name]) {
            setFormErrors({
                ...formErrors,
                [name]: ''
            });
        }
    };

    const validateForm = () => {
        const errors = {};
        
        if (!formData.name.trim()) {
            errors.name = 'Name is required';
        }
        
        if (!formData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Email is invalid';
        }
        
        if (!formData.phone.trim()) {
            errors.phone = 'Phone is required';
        }
        
        if (!formData.title.trim()) {
            errors.title = 'Title is required';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleUnlockCalculator = async (e) => {
        e.preventDefault();
        
        if (validateForm()) {
            setIsSubmitting(true);
            
            try {
                // Send form data to webhook
                const response = await axios.post(
                    WEBHOOK_URL,
                    {
                        name: formData.name,
                        email: formData.email,
                        phone: formData.phone,
                        title: formData.title
                    },
                    {
                        headers: {
                            'Content-Type': 'application/json',
                        }
                    }
                );
                
                // Only unlock if the response is successful (status 200-299)
                if (response.status >= 200 && response.status < 300) {
                    setIsUnlocked(true);
                } else {
                    console.error('Unexpected response status:', response.status);
                    alert('There was an issue submitting your form. Please try again.');
                }
                
            } catch (error) {
                console.error('Error submitting form:', error);
                
                // Show user-friendly error message
                if (error.response) {
                    // Server responded with error status
                    console.error('Server error:', error.response.status, error.response.data);
                    alert('There was an issue submitting your form. Please try again.');
                } else if (error.request) {
                    // Request was made but no response received
                    console.error('Network error:', error.request);
                    alert('Network error. Please check your connection and try again.');
                } else {
                    // Something else happened
                    console.error('Error:', error.message);
                    alert('An unexpected error occurred. Please try again.');
                }
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handleGenerateReport = () => {
        setReportGenerated(true);
    };

    // Load Vidalytics script when calculator is unlocked
    useEffect(() => {
        if (isUnlocked) {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.innerHTML = `
                (function (v, i, d, a, l, y, t, c, s) {
                  y='_'+d.toLowerCase();c=d+'L';if(!v[d]){v[d]={};}if(!v[c]){v[c]={};}if(!v[y]){v[y]={};}var vl='Loader',vli=v[y][vl],vsl=v[c][vl + 'Script'],vlf=v[c][vl + 'Loaded'],ve='Embed';
                  if (!vsl){vsl=function(u,cb){
                    if(t){cb();return;}s=i.createElement("script");s.type="text/javascript";s.async=1;s.src=u;
                    if(s.readyState){s.onreadystatechange=function(){if(s.readyState==="loaded"||s.readyState=="complete"){s.onreadystatechange=null;vlf=1;cb();}};}else{s.onload=function(){vlf=1;cb();};}
                    i.getElementsByTagName("head")[0].appendChild(s);
                  };};
                  vsl(l+'loader.min.js',function(){if(!vli){var vlc=v[c][vl];vli=new vlc();}vli.loadScript(l+'player.min.js',function(){var vec=v[d][ve];t=new vec();t.run(a);});});
                })(window, document, 'Vidalytics', '${VIDALYTICS_EMBED_ID}', '${VIDALYTICS_BASE_URL}');
            `;
            document.body.appendChild(script);

            return () => {
                // Cleanup script when component unmounts
                const existingScript = document.querySelector('script[type="text/javascript"]');
                if (existingScript && existingScript.innerHTML.includes('Vidalytics')) {
                    document.body.removeChild(existingScript);
                }
            };
        }
    }, [isUnlocked]);

    // Hide Help Scout Beacon on this page
    useEffect(() => {
        // Hide the beacon when component mounts
        if (window.Beacon) {
            window.Beacon('destroy');
        }
        
        // Add CSS to hide beacon button if it appears
        const style = document.createElement('style');
        style.id = 'hide-helpscout-beacon';
        style.innerHTML = `
            #beacon-container,
            .BeaconFabButtonFrame,
            [data-beacon-icon] {
                display: none !important;
                visibility: hidden !important;
            }
        `;
        document.head.appendChild(style);

        return () => {
            // Cleanup: remove style and reinitialize beacon when leaving page
            const styleElement = document.getElementById('hide-helpscout-beacon');
            if (styleElement) {
                styleElement.remove();
            }
            
            // Reinitialize beacon for other pages
            if (window.Beacon) {
                window.Beacon('init', 'e9b8014a-5dba-4097-9e69-ae9ae797000a');
            }
        };
    }, []);

    // Add styles for placeholder colors
    useEffect(() => {
        const style = document.createElement('style');
        style.id = 'form-placeholder-styles';
        style.innerHTML = `
            input[name="name"]::placeholder,
            input[name="email"]::placeholder,
            input[name="phone"]::placeholder,
            input[name="title"]::placeholder {
                color: #7143d8;
            }
        `;
        document.head.appendChild(style);

        return () => {
            const styleElement = document.getElementById('form-placeholder-styles');
            if (styleElement) {
                styleElement.remove();
            }
        };
    }, []);

    return (
        <div 
            className="min-h-screen w-full flex flex-col overflow-x-hidden"
            style={{
                marginTop: -40,
                padding: 0,
                backgroundColor: '#ded9fe',
                maxWidth: '100%'
            }}
        >
            {/* Header Banner */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white w-full">
                <div className="max-w-7xl mx-auto text-center py-3 px-4">
                    <p className="text-sm sm:text-lg font-medium">Free Resource for Service-Based Businesses</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 w-full p-0 mb-0 flex justify-center">
                <div className="w-full max-w-7xl mx-auto px-1 sm:px-4">
                    <div 
                        className="w-full h-full flex flex-col lg:grid lg:grid-cols-[1fr_1.5fr] gap-4 lg:gap-8 items-start lg:items-stretch"
                        style={{
                            minHeight: 'calc(100vh - 140px)',
                            marginTop: '20px',
                            boxSizing: 'border-box',
                            maxWidth: '100%'
                        }}
                    >
                        {/* Left Side - Form */}
                        <div 
                            className="rounded-lg p-2 sm:p-6 lg:p-8 w-full h-fit flex flex-col items-center justify-center order-1 lg:order-1 lg:-mt-4 -ml-2 lg:ml-0"
                            style={{
                                backgroundColor: '#ded9fe'
                            }}
                        >
                            <div className="mb-4 sm:mb-8 text-center max-w-lg">
                                <h1 className="text-lg sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-2 sm:mb-4 leading-tight">
                                    Retention ROI Calculator
                                </h1>
                                <h2 className="text-sm sm:text-lg lg:text-xl text-gray-700 mb-2 sm:mb-4 leading-relaxed">
                                    What's the <u>TRUE</u> Cost of Churn in Your Business?
                                </h2>
                                <p className="text-gray-600 leading-relaxed text-xs sm:text-base">
                                    Instantly uncover how much revenue you're losing to churn and how 
                                    much profit you could recover by improving retention just 5–10%.
                                </p>
                            </div>

                            {!isUnlocked ? (
                                <div className="flex flex-col w-full max-w-sm">
                                    <form onSubmit={handleUnlockCalculator} className="space-y-3 sm:space-y-4 w-full">
                                        <div className="flex justify-center">
                                            <input
                                                type="text"
                                                name="name"
                                                placeholder="Name"
                                                value={formData.name}
                                                onChange={handleInputChange}    
                                                className={`w-72 sm:w-80 px-3 sm:px-4 py-2 sm:py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base ${
                                                    formErrors.name ? 'border-red-500' : ''
                                                }`}
                                                style={{
                                                    borderColor: formErrors.name ? '#ef4444' : '#7143d8',
                                                    color: '#7143d8'
                                                }}
                                            />
                                            {formErrors.name && (
                                                <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
                                            )}
                                        </div>

                                        <div className="flex justify-center">
                                            <input
                                                type="email"
                                                name="email"
                                                placeholder="Email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                className={`w-72 sm:w-80 px-3 sm:px-4 py-2 sm:py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base ${
                                                    formErrors.email ? 'border-red-500' : ''
                                                }`}
                                                style={{
                                                    borderColor: formErrors.email ? '#ef4444' : '#7143d8',
                                                    color: '#7143d8'
                                                }}
                                            />
                                            {formErrors.email && (
                                                <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
                                            )}
                                        </div>

                                        <div className="flex justify-center">
                                            <input
                                                type="tel"
                                                name="phone"
                                                placeholder="Phone"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                className={`w-72 sm:w-80 px-3 sm:px-4 py-2 sm:py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base ${
                                                    formErrors.phone ? 'border-red-500' : ''
                                                }`}
                                                style={{
                                                    borderColor: formErrors.phone ? '#ef4444' : '#7143d8',
                                                    color: '#7143d8'
                                                }}
                                            />
                                            {formErrors.phone && (
                                                <p className="text-red-500 text-sm mt-1">{formErrors.phone}</p>
                                            )}
                                        </div>

                                        <div className="flex justify-center">
                                            <input
                                                type="text"
                                                name="title"
                                                placeholder="Title"
                                                value={formData.title}
                                                onChange={handleInputChange}
                                                className={`w-72 sm:w-80 px-3 sm:px-4 py-2 sm:py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base ${
                                                    formErrors.title ? 'border-red-500' : ''
                                                }`}
                                                style={{
                                                    borderColor: formErrors.title ? '#ef4444' : '#7143d8',
                                                    color: '#7143d8'
                                                }}
                                            />
                                            {formErrors.title && (
                                                <p className="text-red-500 text-sm mt-1">{formErrors.title}</p>
                                            )}
                                        </div>

                                        <div className="flex justify-center">
                                            <button
                                                type="submit"
                                                disabled={isSubmitting}
                                                className={`w-72 sm:w-80 bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 sm:py-4 px-4 sm:px-6 rounded-lg font-semibold text-base sm:text-lg shadow-md hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105 ${
                                                    isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                                                }`}
                                            >
                                            <div className="flex flex-col items-center">
                                                <span className="text-white">{isSubmitting ? 'Submitting...' : 'Unlock Calculator'}</span>
                                                {!isSubmitting && (
                                                    <span className="text-xs mt-1 opacity-90 text-white">
                                                        Result Will Be Sent to the Email You Provide
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                        </div>

                                    </form>
                                    
                                    <p className="text-xs sm:text-sm text-center mt-2 sm:mt-3" style={{ color: '#7143d8' }}>
                                        Completing this form will unlock this free on-screen calculator
                                    </p>
                                    <div className="text-xs text-left sm:mt-3 space-y-1 w-full max-w-lg mx-auto px-1 sm:px-4" style={{ color: '#000000ff' }}>
                                        <p className="font-bold">With this calculator you'll instantly see:</p>
                                        <div className="flex items-start gap-2 font-normal">
                                            <span className="text-yellow-500 flex-shrink-0 mt-0.5">⚡</span>
                                            <span>Your hidden churn cost (most owners underestimate it by up to 70%)</span>
                                        </div>
                                        <div className="flex items-start gap-2 font-normal">
                                            <span className="text-yellow-500 flex-shrink-0 mt-0.5">⚡</span>
                                            <span>How much profit you recover by improving retention even slightly.</span>
                                        </div>
                                        <div className="flex items-start gap-2 font-normal">
                                            <span className="text-yellow-500 flex-shrink-0 mt-0.5">⚡</span>
                                            <span>Your new client lifetime value if retention improves.</span>
                                        </div>
                                        <div className="flex items-start gap-2 font-normal">
                                            <span className="text-yellow-500 flex-shrink-0 mt-0.5">⚡</span>
                                            <span>How much you can reduce new customer acquisition spend.</span>
                                        </div>
                                        <div className="flex items-start gap-2 font-normal">
                                            <span className="text-yellow-500 flex-shrink-0 mt-0.5">⚡</span>
                                            <span>Your projected revenue growth from retention fixes.</span>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="w-full flex flex-col items-center justify-center">
                                    {/* video container */}
                                    <div 
                                        className="w-full max-w-xs sm:max-w-md mx-auto rounded-lg mb-12 sm:mb-24 relative group"
                                        style={{
                                            height: '200px',
                                            backgroundColor: '#f3f4f6'
                                        }}
                                    >
                                        <div id={VIDALYTICS_EMBED_ID} style={{width: '100%', position: 'relative', paddingTop: '56.25%'}}></div>
                                        
                                        {/* Fullscreen button - Desktop only */}
                                        <button
                                            className="hidden sm:flex absolute top-2 right-2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded transition-opacity duration-200 items-center justify-center opacity-0 group-hover:opacity-100"
                                            onClick={() => {
                                                const videoContainer = document.getElementById(VIDALYTICS_EMBED_ID);
                                                if (videoContainer) {
                                                    // Detect if it's mobile
                                                    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                                                    
                                                    // Disabled for mobile devices
                                                    if (isMobile) {
                                                        return;
                                                    }
                                                    
                                                    // Full screen for desktop only
                                                    if (videoContainer.requestFullscreen) {
                                                        videoContainer.requestFullscreen();
                                                    } else if (videoContainer.webkitRequestFullscreen) {
                                                        videoContainer.webkitRequestFullscreen();
                                                    } else if (videoContainer.msRequestFullscreen) {
                                                        videoContainer.msRequestFullscreen();
                                                    }
                                                }
                                            }}
                                            style={{ zIndex: 10 }}
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                                                <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                                            </svg>
                                        </button>
                                    </div>
                                    
                                    {!reportGenerated ? (
                                        <div className="w-full max-w-xs sm:max-w-md mx-auto space-y-3 sm:space-y-4 text-center">
                                            <div className="bg-white bg-opacity-50 rounded-lg p-3 sm:p-4">
                                                <p style={{ color: '#7143d8', fontWeight: 'bold' }} className="text-xs sm:text-sm leading-relaxed">
                                                    <span style={{ color: '#7143d8' }} className="font-extrabold">Step 1:</span> Watch the tutorial above so you get the most accurate report
                                                </p>
                                            </div>
                                            
                                            <div className="bg-white bg-opacity-50 rounded-lg p-3 sm:p-4">
                                                <p style={{ color: '#7143d8', fontWeight: 'bold' }} className="text-xs sm:text-sm leading-relaxed">
                                                    <span style={{ color: '#7143d8' }} className="font-extrabold">Step 2:</span> Enter your business information into the calculator
                                                </p>
                                            </div>
                                            
                                            <div className="bg-white bg-opacity-50 rounded-lg p-3 sm:p-4">
                                                <p style={{ color: '#7143d8', fontWeight: 'bold' }} className="text-xs sm:text-sm leading-relaxed">
                                                    <span style={{ color: '#7143d8' }} className="font-extrabold">Step 3:</span> Your results will instantly be displayed and a more in depth analysis will be sent with recommendations to the email you provided.
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="w-full max-w-xs sm:max-w-md mx-auto flex justify-center mt-8 sm:mt-16">
                                            <button
                                                className="px-4 sm:px-6 py-3 sm:py-4 text-white font-bold rounded-lg text-sm sm:text-base shadow-lg hover:shadow-xl transition-all duration-200 w-full max-w-xs"
                                                style={{
                                                    backgroundColor: '#10b981',
                                                    fontWeight: 'bold'
                                                }}
                                                onClick={() => {
                                                    setShowBookingModal(true);
                                                }}
                                            >
                                                Schedule Your Free Retention Analysis Call
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Right Side - Calculator */}
                        <div className="relative w-full h-full order-2 lg:order-2 flex justify-center items-start lg:ml-4">
                            <div 
                                className="rounded-lg p-2 sm:p-4 w-full max-w-4xl mx-auto"
                                style={{
                                    backgroundColor: '#ded9fe',
                                    minHeight: '500px',
                                    position: 'relative',
                                    overflow: 'visible',
                                    maxWidth: '100%'
                                }}
                            >
                                <RetentionCalculator isPublicMode={true} isUnlocked={isUnlocked} onGenerateReport={handleGenerateReport} userEmail={formData.email} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile CTA Button - Only visible on mobile after report is generated */}
            {reportGenerated && (
                <div className="lg:hidden w-full max-w-xs sm:max-w-md mx-auto flex justify-center mt-4 sm:mt-8 mb-8 sm:mb-12" style={{ backgroundColor: '#ded9fe' }}>
                    <button
                        className="px-4 sm:px-6 py-3 sm:py-4 text-white font-bold rounded-lg text-sm sm:text-base shadow-lg hover:shadow-xl transition-all duration-200 w-full max-w-xs"
                        style={{
                            backgroundColor: '#10b981',
                            fontWeight: 'bold'
                        }}
                        onClick={() => {
                            setShowBookingModal(true);
                        }}
                    >
                        Schedule Your Free Retention Analysis Call
                    </button>
                </div>
            )}

            {/* Booking Modal */}
            {showBookingModal && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4"
                    onClick={() => setShowBookingModal(false)}
                >
                    <div 
                        className="bg-white rounded-lg p-3 sm:p-6 w-full max-h-[95vh] overflow-hidden relative flex flex-col"
                        style={{ maxWidth: '1400px', height: '90vh' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close button */}
                        <button
                            onClick={() => setShowBookingModal(false)}
                            className="absolute top-2 sm:top-4 right-2 sm:right-4 text-white hover:text-gray-200 font-bold z-10 rounded transition-all duration-200 hover:bg-purple-700"
                            style={{
                                backgroundColor: '#7143d8',
                                width: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '18px'
                            }}
                        >
                            ×
                        </button>
                        
                        {/* Modal title */}
                        <div className="pr-8 sm:pr-12 text-center mb-4" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '40px' }}>
                            <h2 className="text-lg sm:text-2xl font-bold" style={{ color: '#7143d8' }}>Book Your ClientBloom Demo Here</h2>
                        </div>
                        
                        {/* Iframe container */}
                        <div className="w-full flex-1 flex justify-center items-center" style={{ position: 'relative' }}>
                            <div className="w-full max-w-5xl mx-auto" style={{ height: '100%' }}>
                                <iframe 
                                    src={BOOKING_WIDGET_URL} 
                                    style={{ 
                                        width: '100%', 
                                        height: '100%',
                                        border: 'none',
                                        overflow: 'hidden',
                                        minHeight: '600px'
                                    }} 
                                    scrolling="no" 
                                    id="bM7RFpLdUbQLf8FzoA9m_1761689425953"
                                    title="ClientBloom Booking Widget"
                                />
                            </div>
                            <script src={FORM_EMBED_SCRIPT_URL} type="text/javascript"></script>
                        </div>
                    </div>
                </div>
            )}

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default PublicRetentionCalculator;
